import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import color from "../../../Constants/colors";
import { useDebouncedCallback } from "../../../Hooks/useDebouncedCallback";
import { DocumentCraftingUser } from "../../../Models/documentCrafting";
import { useDocumentCraftingMembers } from "../../../Query/useDocumentCrafting";
import { displayName } from "../Common/format";

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  label: string;
  /** Users not to offer (the owner, people already in the list). */
  excludeIds: number[];
  onSelect: (user: DocumentCraftingUser) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

/** Search over the active members of the workspace (`GET /members/?q=`);
 * picking one hands it on and clears the field. */
const MemberSearch: React.FC<Props> = ({
  label,
  excludeIds,
  onSelect,
  disabled,
  autoFocus,
}) => {
  const { t } = useTranslation("documentCrafting");
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const updateQuery = useDebouncedCallback(
    (text: string) => setQuery(text.trim()),
    SEARCH_DEBOUNCE_MS,
  );
  const members = useDocumentCraftingMembers(query, !disabled);
  const options = (members.data?.members ?? []).filter(
    (member) => !excludeIds.includes(member.id),
  );

  return (
    <Autocomplete<DocumentCraftingUser, false, false, false>
      options={options}
      value={null}
      inputValue={input}
      disabled={disabled}
      onInputChange={(_event, text, reason) => {
        if (reason === "reset") {
          setInput("");
          return;
        }
        setInput(text);
        updateQuery(text);
      }}
      onChange={(_event, user) => {
        if (user) onSelect(user);
        setInput("");
      }}
      // The server searches; keep its order and matches.
      filterOptions={(all) => all}
      loading={members.isFetching}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionLabel={(option) => displayName(option)}
      noOptionsText={t("detail.share.noMembers")}
      loadingText={t("detail.share.searching")}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
          key: React.Key;
        };
        return (
          <Box component="li" key={key ?? option.id} {...optionProps}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, color: color.gray900 }} noWrap>
                {displayName(option)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: color.gray500 }} noWrap>
                {option.email}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          autoFocus={autoFocus}
          placeholder={t("detail.share.searchPlaceholder")}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {members.isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{ ...params.inputProps, autoComplete: "off" }}
        />
      )}
    />
  );
};

export default MemberSearch;
